import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteData, fetchDataFromApi, postData } from "../../utils/api";

export const fetchWishlistItems = createAsyncThunk(
  "wishlist/fetchWishlistItems",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetchDataFromApi(`/api/my-list?userId=${userId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch wishlist items");
    }
  }
);

export const addItemToWishlist = createAsyncThunk(
  "wishlist/addItemToWishlist",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await postData("/api/my-list/add", { productId });
      if (!response.success) {
        return rejectWithValue(
          response.message || "Failed to add item to wishlist"
        );
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const updatedWishlist = await fetchDataFromApi(
        `/api/my-list?userId=${user._id}`
      );
      return Array.isArray(updatedWishlist) ? updatedWishlist : [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add item to wishlist");
    }
  }
);

export const removeItemFromWishlist = createAsyncThunk(
  "wishlist/removeItemFromWishlist",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await deleteData(`/api/my-list/${productId}`);
      if (!response.success) {
        return rejectWithValue(
          response.message || "Failed to remove item from wishlist"
        );
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const updatedWishlist = await fetchDataFromApi(
        `/api/my-list?userId=${user._id}`
      );
      return Array.isArray(updatedWishlist) ? updatedWishlist : [];
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to remove item from wishlist"
      );
    }
  }
);

export const toggleWishlistItem = createAsyncThunk(
  "wishlist/toggleWishlistItem",
  async (productId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const existingItem = state.wishlist.wishlistItems.find(
        (item) => item.productId?._id === productId
      );

      if (existingItem) {
        const response = await deleteData(
          `/api/my-list/${existingItem.productId._id}`
        );
        if (!response.success) {
          return rejectWithValue(
            response.message || "Failed to remove item from wishlist"
          );
        }
        return { removed: true, productId: existingItem.productId._id };
      } else {
        const response = await postData("/api/my-list/add", { productId });
        if (!response.success) {
          return rejectWithValue(
            response.message || "Failed to add item to wishlist"
          );
        }
        return { added: true, item: response.data };
      }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to toggle wishlist item");
    }
  }
);

const initialState = {
  wishlistItems: [],
  loading: false,
  error: null,
};

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    setWishlistItems: (state, action) => {
      state.wishlistItems = Array.isArray(action.payload) ? action.payload : [];
      state.loading = false;
      state.error = null;
    },
    addToWishlist: (state, action) => {
      state.wishlistItems.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    removeFromWishlist: (state, action) => {
      state.wishlistItems = state.wishlistItems.filter(
        (item) => item._id !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearWishlist: (state) => {
      state.wishlistItems = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchWishlistItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlistItems.fulfilled, (state, action) => {
        state.wishlistItems = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchWishlistItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addItemToWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItemToWishlist.fulfilled, (state, action) => {
        state.wishlistItems = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(addItemToWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeItemFromWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeItemFromWishlist.fulfilled, (state, action) => {
        state.wishlistItems = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(removeItemFromWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(toggleWishlistItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleWishlistItem.fulfilled, (state, action) => {
        if (action.payload.removed) {
          state.wishlistItems = state.wishlistItems.filter(
            (item) => item.productId?._id !== action.payload.productId
          );
        } else if (action.payload.added) {
          state.wishlistItems.push(action.payload.item);
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(toggleWishlistItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setWishlistItems,
  addToWishlist,
  removeFromWishlist,
  setLoading,
  setError,
  clearWishlist,
} = wishlistSlice.actions;

export default wishlistSlice.reducer;
